import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { EventRow, WineRow } from '../api/models';
import { Button, useConfirm } from './ui';
import { TestEventBadge } from './TestEventBadge';

/**
 * Switch a tasting between a rehearsal and the real thing.
 *
 * Only through `PUT /events/:id/test-mode`, never the event form: the flip
 * decides whether the night enters the club's record and who its messages
 * reach, so it gets its own confirm. It locks once results have gone out —
 * a final tasting, or one reopened after its finalize (frozen ranks during the
 * reveal). The server enforces the same rule; this just doesn't offer it.
 */
export function TestModeToggle({
  event,
  wines,
}: {
  event: Pick<EventRow, 'id' | 'isTest' | 'phase'>;
  wines: Pick<WineRow, 'groupRank'>[];
}) {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const isTest = !!event.isTest;
  const locked =
    event.phase === 'final' ||
    (event.phase === 'revealing' && wines.some((w) => w.groupRank != null));

  const flip = useMutation({
    mutationFn: (next: boolean) =>
      api.put<{ data: { event: EventRow } }>(`/events/${event.id}/test-mode`, { isTest: next }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['event', event.id] });
      void qc.invalidateQueries({ queryKey: ['events'] });
      // The composer's reach is clamped to the roster only while it's a test.
      void qc.invalidateQueries({ queryKey: ['event-comms', event.id] });
    },
  });

  if (locked) {
    if (!isTest) return null;
    return (
      <div className="mb-6 flex flex-wrap items-center gap-3 text-xs text-cream-300/60">
        <TestEventBadge isTest />
        <span>Its results have gone out, so it stays a test event.</span>
      </div>
    );
  }

  const ask = async () => {
    const ok = await confirm(
      isTest
        ? {
            title: 'Make this a real tasting?',
            description:
              'It joins the club’s record — the archive, statistics and exports — and from now on its announcements, reminders and results go to members, not just the testers on its roster.',
            confirmLabel: 'Make it real',
          }
        : {
            title: 'Make this a test event?',
            description:
              'Members can still see it, marked as a test, but it drops out of the archive, statistics and exports, and its messages reach only the testers you add to it. You can switch it back until the results go out.',
            confirmLabel: 'Make it a test event',
          },
    );
    if (ok) flip.mutate(!isTest);
  };

  return (
    <div
      className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
        isTest ? 'border-warning/30 bg-warning/5' : 'border-ink-800'
      }`}
    >
      <div className="min-w-[14rem] flex-1 text-sm">
        {isTest ? (
          <>
            <TestEventBadge isTest />
            <p className="mt-1 text-xs text-cream-300/70">
              A rehearsal: left out of the club’s record, and its messages reach only its testers.
            </p>
          </>
        ) : (
          <p className="text-xs text-cream-300/60">
            Rehearsing or training someone? Make this a test event so it stays out of the record and
            doesn’t message the club.
          </p>
        )}
      </div>
      <Button variant={isTest ? 'primary' : 'ghost'} className="text-sm" loading={flip.isPending} onClick={ask}>
        {isTest ? 'Make it a real tasting' : 'Make this a test event'}
      </Button>
    </div>
  );
}
